import type { TaskNode } from "./types";

export interface Located {
  node: TaskNode;
  parentList: TaskNode[];
  index: number;
}

// find a node by id anywhere in the tree; returns the node, its immediate list, and
// index — or null when not found.
export function locateOrNull(list: TaskNode[], id: string): Located | null {
  for (let i = 0; i < list.length; i++) {
    const node = list[i];
    if (node.id === id) return { node, parentList: list, index: i };
    const found = locateOrNull(node.children, id);
    if (found) return found;
  }
  return null;
}

// like locateOrNull but throws when the id isn't in the tree — use when the caller
// knows the node must exist (keeps call sites free of `locate(...)!.node`).
export function locate(list: TaskNode[], id: string): Located {
  const found = locateOrNull(list, id);
  if (!found) throw new Error("locate: task not found: " + id);
  return found;
}

export interface ParentLoc {
  parentNode: TaskNode;
  grandList: TaskNode[];
  parentIndex: number;
}

export function findParent(
  list: TaskNode[],
  id: string,
  grandList: TaskNode[] | null = null,
  parentNode: TaskNode | null = null,
  parentIndex = -1,
): ParentLoc | null {
  for (let i = 0; i < list.length; i++) {
    if (list[i].id === id) {
      if (parentNode === null) return null; // it's a root
      return { parentNode, grandList: grandList as TaskNode[], parentIndex };
    }
    const r = findParent(list[i].children, id, list, list[i], i);
    if (r) return r;
  }
  return null;
}

export function isDescendant(tree: TaskNode[], ancestorId: string, maybeChildId: string): boolean {
  const a = locateOrNull(tree, ancestorId);
  if (!a) return false;
  return !!locateOrNull(a.node.children, maybeChildId);
}

// disabled subtrees don't count toward their parent's roll-up
export function childSum(node: TaskNode): number {
  return node.children.reduce((s, c) => s + (c.disabled ? 0 : rollup(c)), 0);
}

export function rollup(node: TaskNode): number {
  if (node.children.length === 0) return node.estimate || 0;
  const cs = childSum(node);
  if (cs > 0) return cs;
  // children present but summing to 0: stay 0 when nothing is active (all disabled);
  // only fall back to the node's own estimate when active children just carry no estimate
  return leafDoneCounts(node).totalN === 0 ? 0 : (node.estimate || 0);
}

export function totalEstimate(tree: TaskNode[]): number {
  return tree.reduce((s, n) => s + (n.disabled ? 0 : rollup(n)), 0);
}

// estimate-days that are switched off (disabled tasks / subtrees)
export function disabledEstimate(tree: TaskNode[]): number {
  let d = 0;
  (function walk(list: TaskNode[], ancestorDisabled: boolean) {
    list.forEach((n) => {
      const dis = ancestorDisabled || n.disabled;
      if (n.children.length === 0) { if (dis) d += n.estimate || 0; }
      else walk(n.children, dis);
    });
  })(tree, false);
  return d;
}

// count of leaf tasks under a node and how many are marked done (disabled excluded)
export function leafDoneCounts(node: TaskNode): { doneN: number; totalN: number } {
  let doneN = 0, totalN = 0;
  (function walk(n: TaskNode) {
    if (n.disabled) return;
    if (n.children.length === 0) { totalN++; if (n.done) doneN++; }
    else n.children.forEach(walk);
  })(node);
  return { doneN, totalN };
}

// aggregate leaf done-counts across a whole forest (used by the report's "anything done?")
export function treeDoneCounts(tree: TaskNode[]): { doneN: number; totalN: number } {
  let doneN = 0, totalN = 0;
  for (const n of tree) { const c = leafDoneCounts(n); doneN += c.doneN; totalN += c.totalN; }
  return { doneN, totalN };
}

// a node is "done" when it has active leaves and all of them are done
export function isDone(node: TaskNode): boolean {
  const c = leafDoneCounts(node);
  return c.totalN > 0 && c.doneN === c.totalN;
}

// Per-node roll-up metrics, computed for the WHOLE tree in one bottom-up pass.
// Mirrors childSum/rollup/leafDoneCounts/isDone exactly, so the render loop can look
// each node up in O(1) instead of re-walking every subtree per row (was O(n²)).
export interface NodeMetrics {
  childSum: number;  // Σ rollup of non-disabled children (0 for a leaf)
  rollup: number;    // rollup(node)
  remaining: number; // rollup of the NOT-done, active leaves only (incomplete work)
  doneDays: number;  // rollup of the DONE, active leaves only (finished work)
  doneN: number;     // leafDoneCounts(node).doneN
  totalN: number;    // leafDoneCounts(node).totalN
  done: boolean;     // isDone(node)
  unestimated: number; // count of active (non-disabled) leaves in the subtree with a 0 estimate
}

export function computeMetrics(tree: TaskNode[]): Map<string, NodeMetrics> {
  const map = new Map<string, NodeMetrics>();
  function walk(node: TaskNode): NodeMetrics {
    let childSum = 0, doneN = 0, totalN = 0, remaining = 0, doneDays = 0, unestimated = 0;
    for (const c of node.children) {
      const cm = walk(c); // recurse first so children land in the map
      if (!c.disabled) { childSum += cm.rollup; doneN += cm.doneN; totalN += cm.totalN; remaining += cm.remaining; doneDays += cm.doneDays; unestimated += cm.unestimated; }
    }
    // leafDoneCounts: a node that is ITSELF disabled early-returns {0,0}; a leaf counts 1.
    if (node.disabled) { doneN = 0; totalN = 0; }
    else if (node.children.length === 0) { totalN = 1; doneN = node.done ? 1 : 0; }
    // rollup: leaf → own estimate; parent → childSum, else own estimate only when it has
    // active leaves (totalN>0 per leafDoneCounts), else 0.
    const rollup = node.children.length === 0
      ? (node.estimate || 0)
      : (childSum > 0 ? childSum : (totalN === 0 ? 0 : (node.estimate || 0)));
    // remaining = incomplete active leaf-days (a done or disabled leaf contributes 0)
    if (node.children.length === 0) remaining = node.disabled || node.done ? 0 : (node.estimate || 0);
    // doneDays = finished active leaf-days — the other half of the split. NOTE: it pairs with
    // `rollup` (total), not with `remaining`: for the parent-estimate fallback above, rollup-doneDays
    // is the honest "left", while remaining is 0 because no leaf carries days.
    if (node.children.length === 0) doneDays = !node.disabled && node.done ? (node.estimate || 0) : 0;
    // unestimated: a leaf that is still open (active, not done) and carries no estimate
    if (node.children.length === 0) unestimated = node.disabled || node.done || node.estimate ? 0 : 1;
    const m: NodeMetrics = { childSum, rollup, remaining, doneDays, doneN, totalN, done: totalN > 0 && doneN === totalN, unestimated };
    map.set(node.id, m);
    return m;
  }
  tree.forEach(walk);
  return map;
}

// Per-node per-stream rollups (aligned to `estimations`), one bottom-up pass: leaf → its own
// stream values; parent → element-wise sum of its non-disabled children. Used to fill the
// per-stream estimate columns on parent rows (leaf rows show the editable values directly).
export function computeStreamRollups(tree: TaskNode[], estimations: string[]): Map<string, number[]> {
  const map = new Map<string, number[]>();
  function walk(node: TaskNode): number[] {
    let vals: number[];
    if (node.children.length === 0) {
      vals = estimations.map((s) => node.estimates[s] || 0);
    } else {
      vals = estimations.map(() => 0);
      for (const c of node.children) {
        const cv = walk(c);
        if (!c.disabled) for (let i = 0; i < vals.length; i++) vals[i] += cv[i];
      }
    }
    map.set(node.id, vals);
    return vals;
  }
  tree.forEach(walk);
  return map;
}

// Per-node "latest version in the subtree" (self + descendants), one bottom-up pass. "Latest" = the
// version with the highest index in the configured `versions` order; empty and used-but-undefined
// versions (indexOf === -1) are ignored — same ranking as the by-feature max in tags.ts. Used to show
// a faint shadow hint on a versionless group of what release its work targets (never assigned).
export function computeShadowVersions(tree: TaskNode[], versions: string[]): Map<string, string> {
  const map = new Map<string, string>();
  function walk(node: TaskNode): string {
    let bestIdx = versions.indexOf(node.version);
    let bestVer = bestIdx >= 0 ? node.version : "";
    for (const c of node.children) {
      const cv = walk(c); // already resolved to a ranked version or ""
      const ci = versions.indexOf(cv);
      if (ci > bestIdx) { bestIdx = ci; bestVer = cv; }
    }
    map.set(node.id, bestVer);
    return bestVer;
  }
  tree.forEach(walk);
  return map;
}

// overall progress in estimate-days across all active leaves
export function progress(tree: TaskNode[]): { done: number; total: number; pct: number } {
  let done = 0, total = 0;
  (function walk(list: TaskNode[]) {
    list.forEach((n) => {
      if (n.disabled) return;
      if (n.children.length === 0) { const e = n.estimate || 0; total += e; if (n.done) done += e; }
      else walk(n.children);
    });
  })(tree);
  return { done, total, pct: total ? Math.round((done / total) * 100) : 0 };
}

// active leaves marked done that carry NO estimate. They move no days, so estimate-weighted
// progress can't see them at all — a plan with few estimates reads 0% however much is finished.
// The dashboard uses this to draw the "done, but worth 0 days" part of a card's bar.
export function doneUnestimatedCount(tree: TaskNode[]): number {
  let n = 0;
  (function walk(list: TaskNode[]) {
    list.forEach((node) => {
      if (node.disabled) return;
      if (node.children.length === 0) { if (node.done && !(node.estimate || 0)) n++; }
      else walk(node.children);
    });
  })(tree);
  return n;
}

export function anyExpanded(tree: TaskNode[]): boolean {
  let found = false;
  (function walk(list: TaskNode[]) {
    for (const n of list) { if (n.children.length && !n.collapsed) found = true; walk(n.children); }
  })(tree);
  return found;
}

export function setAllCollapsed(tree: TaskNode[], val: boolean): void {
  (function walk(list: TaskNode[]) {
    for (const n of list) { if (n.children.length) n.collapsed = val; walk(n.children); }
  })(tree);
}

// map each leaf node id -> its 1-based sequence number (pre-order, matches leafLines)
export function leafIndexMap(tree: TaskNode[]): Map<string, number> {
  const map = new Map<string, number>();
  let n = 0;
  (function walk(list: TaskNode[]) {
    list.forEach((node) => {
      if (node.children.length === 0) map.set(node.id, ++n);
      else walk(node.children);
    });
  })(tree);
  return map;
}

// does any node in the tree satisfy the predicate? (drives the filter empty-state)
export function anyMatch(tree: TaskNode[], match: (n: TaskNode) => boolean): boolean {
  let found = false;
  (function walk(list: TaskNode[]) {
    for (const n of list) { if (match(n)) found = true; walk(n.children); }
  })(tree);
  return found;
}

// The set of node ids visible when a predicate-based tree filter is active. A node is visible
// if it MATCHES, is an ANCESTOR of a match (context path), or a DESCENDANT of a match (a matched
// group reveals its whole subtree). Two passes: mark subtree-matches bottom-up, then walk down
// carrying `ancestorMatch` — visible = ancestorMatch || some descendant (incl. self) matches.
export function filterVisibleSet(tree: TaskNode[], match: (n: TaskNode) => boolean): Set<string> {
  const inSub = new Set<string>(); // node or any descendant matches
  (function mark(list: TaskNode[]): boolean {
    let any = false;
    for (const n of list) {
      const childHit = mark(n.children);
      if (match(n) || childHit) { inSub.add(n.id); any = true; }
    }
    return any;
  })(tree);
  const visible = new Set<string>();
  (function walk(list: TaskNode[], ancestorMatch: boolean) {
    for (const n of list) {
      const self = ancestorMatch || inSub.has(n.id);
      if (self) visible.add(n.id);
      walk(n.children, ancestorMatch || match(n));
    }
  })(tree, false);
  return visible;
}
